import { AxiosInstance } from 'axios'
import {
    Role,
    RoleCreate,
    RoleUpdate,
    ListRolesParams,
    PaginatedRoleResponse,
    ReplacePermissionsPayload,
    AddPermissionsPayload,
    AssignUserPayload,
} from './role.type'

export class RoleApi {
    constructor(private readonly client: AxiosInstance) {}

    /**
     * Get all roles with pagination
     */
    async list(params?: ListRolesParams): Promise<PaginatedRoleResponse> {
        const response = await this.client.get<PaginatedRoleResponse>('/roles', {
            params,
        })
        return response.data
    }

    /**
     * Get a single role by ID
     */
    async findOne(id: string): Promise<Role> {
        const response = await this.client.get<Role>(`/roles/${id}`)
        return response.data
    }

    /**
     * Create a new role
     */
    async create(payload: RoleCreate): Promise<Role> {
        const response = await this.client.post<Role>('/roles', payload)
        return response.data
    }

    /**
     * Update a role
     */
    async update(id: string, payload: RoleUpdate): Promise<Role> {
        const response = await this.client.put<Role>(`/roles/${id}`, payload)
        return response.data
    }

    /**
     * Delete a role (soft delete)
     */
    async delete(id: string): Promise<void> {
        await this.client.delete(`/roles/${id}`)
    }

    /**
     * Replace all permissions for a role
     */
    async replacePermissions(id: string, payload: ReplacePermissionsPayload): Promise<void> {
        await this.client.put(`/roles/${id}/permissions`, payload)
    }

    /**
     * Add permissions to a role
     */
    async addPermissions(id: string, payload: AddPermissionsPayload): Promise<void> {
        await this.client.post(`/roles/${id}/permissions`, payload)
    }

    /**
     * Remove a permission from a role
     */
    async removePermission(roleId: string, permissionId: string): Promise<void> {
        await this.client.delete(`/roles/${roleId}/permissions/${permissionId}`)
    }

    /**
     * Assign a user to a role
     */
    async assignUser(roleId: string, payload: AssignUserPayload): Promise<void> {
        await this.client.post(`/roles/${roleId}/users`, payload)
    }

    /**
     * Remove a user from a role
     */
    async removeUser(roleId: string, userId: string, projectId?: string): Promise<void> {
        await this.client.delete(`/roles/${roleId}/users/${userId}`, {
            params: projectId ? { projectId } : undefined,
        })
    }
}
